import { useCallback, useMemo } from 'react'
import { useLoggedInUser } from '../../../../app/hooks/useLoggedInUser'
import {
  useCreateProductCategoryMutation,
  useGetProductCategoriesQuery,
  useGetProductCategoryQuery,
  useUpdateProductCategoryMutation,
} from '../../../../app/redux-fetch/apiProducts'
import { IProductCategory } from '../../types'

interface UseProductCategoryProps {
  customerId: number
  productCategoryId?: number
}

interface UseProductCategoryReturn {
  productCategory: IProductCategory | undefined
  productCategories: IProductCategory[]
  isLoading: boolean
  isError: boolean
  createProductCategory: (data: IProductCategory) => Promise<void>
  updateProductCategory: (data: IProductCategory) => Promise<void>
}

export const useProductCategory = ({
  customerId,
  productCategoryId,
}: UseProductCategoryProps): UseProductCategoryReturn => {
  const { tenantId } = useLoggedInUser()

  const {
    data: productCategory,
    isLoading: isCategoryLoading,
    isError: isCategoryError,
  } = useGetProductCategoryQuery(
    {
      tenantId,
      customerId,
      categoryId: productCategoryId!,
    },
    { skip: !productCategoryId || !customerId || customerId < 1 }
  )

  const {
    data: productCategories = [],
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
  } = useGetProductCategoriesQuery(
    { tenantId, customerId },
    { skip: !customerId || customerId < 1 }
  )

  const [createMutation] = useCreateProductCategoryMutation()
  const [updateMutation] = useUpdateProductCategoryMutation()

  const createProductCategory = useCallback(
    async (data: IProductCategory) => {
      await createMutation({
        tenantId,
        customerId: data.customerId ?? customerId,
        body: { ...data, customerId: data.customerId ?? customerId },
      }).unwrap()
    },
    [createMutation, tenantId, customerId]
  )

  const updateProductCategory = useCallback(
    async (data: IProductCategory) => {
      if (!data.id) throw new Error('Product category ID is required for update')

      await updateMutation({
        tenantId,
        customerId: data.customerId ?? customerId,
        productId: data.id,
        body: { ...data, customerId: data.customerId ?? customerId },
      }).unwrap()
    },
    [updateMutation, tenantId, customerId]
  )

  return useMemo(
    () => ({
      productCategory,
      productCategories,
      isLoading: isCategoryLoading || isCategoriesLoading,
      isError: isCategoryError || isCategoriesError,
      createProductCategory,
      updateProductCategory,
    }),
    [
      productCategory,
      productCategories,
      isCategoryLoading,
      isCategoriesLoading,
      isCategoryError,
      isCategoriesError,
      createProductCategory,
      updateProductCategory,
    ]
  )
}
